import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock as ClockIcon, Check, Trash2 } from 'lucide-react';
import Clock from '../../../../../components/forms/Clock';

const ParticularModal = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  currentParticular = null,
  dayNumber,
  isNew = false
}) => {
  const [title, setTitle] = useState('');
  const [time, setTime] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (isOpen) {
      setTitle(currentParticular?.title || currentParticular?.activity || '');
      setTime(currentParticular?.time || '');
      setDescription(currentParticular?.description || '');
    }
  }, [isOpen, currentParticular]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSave({
      title: title.trim(),
      activity: title.trim(),
      time: time.trim() || null,
      description: description.trim()
    });
    onClose();
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 md:p-6 bg-slate-900/50 backdrop-blur-xs animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3.5rem)] my-auto transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-100 bg-white shrink-0">
          <div>
            <span className="text-[11px] font-medium text-slate-400 block">
              Day {dayNumber} Event
            </span>
            <h3 className="text-sm sm:text-base font-semibold text-slate-800 tracking-tight">
              {isNew ? 'Add Event' : 'Edit Event'}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
          {/* Title */}
          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">
              Event Title <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. City tour with guide, Airport pickup, or Amer Fort visit"
              className="w-full px-3.5 h-[38px] bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs"
            />
          </div>

          {/* Time */}
          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1 flex items-center gap-1">
              <ClockIcon size={11} className="text-[#067978]" /> Event Time (24h)
            </label>
            <Clock
              value={time}
              onChange={(val) => setTime(val || '')}
              placeholder="09:30"
              inputClassName="h-[38px] px-3 rounded-xl text-xs font-normal text-slate-800 outline-none border-slate-200"
            />
          </div>

          {/* Details / Description */}
          <div>
            <label className="block text-[11px] font-medium text-slate-600 mb-1">
              Description / Route Details <span className="text-slate-400 font-normal">(Optional)</span>
            </label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Changi Airport T3 to Hotel Boss. Private chauffeur escort with cold refreshments."
              className="w-full px-3.5 py-2.5 bg-white border border-slate-200 focus:border-[#067978] rounded-xl text-xs font-normal text-slate-800 outline-none transition-all shadow-2xs resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            {!isNew && onDelete ? (
              <button
                type="button"
                onClick={() => {
                  onDelete();
                  onClose();
                }}
                className="inline-flex items-center gap-1 text-xs font-medium text-rose-600 hover:text-rose-700 hover:underline cursor-pointer"
              >
                <Trash2 size={13} />
                <span>Remove</span>
              </button>
            ) : (
              <div />
            )}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-1.5 bg-[#067978] hover:bg-[#056564] text-white text-xs font-medium rounded-lg shadow-xs transition-all active:scale-95 cursor-pointer flex items-center gap-1.5"
              >
                <Check size={13} />
                <span>{isNew ? 'Add Event' : 'Save'}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ParticularModal;
